import type defaultLocale from '@readme/i18n/locales/default';
import type { HTMLAttributes, ElementType, PropsWithChildren, FC } from 'react';

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatAbsoluteTime, formatRelativeTime } from '@core/utils/i18nDateTime';
import Icon from '@ui/Icon';
import Tooltip from '@ui/Tooltip';

import './style.scss';

function getTimeLocaleKeys(attributed: boolean, prefix?: 'posted' | 'updated') {
  let key: keyof (typeof defaultLocale)['time']['absolute' | 'justNow' | 'relative'];

  switch (prefix) {
    case 'posted':
      key = attributed ? 'postedPrefixAttributed' : 'postedPrefix';
      break;
    case 'updated':
      key = attributed ? 'updatedPrefixAttributed' : 'updatedPrefix';
      break;
    default:
      key = attributed ? 'noPrefixAttributed' : 'noPrefix';
  }

  return {
    justNow: key,
    relative: key,
  };
}

interface LocalizedDateLineProps extends HTMLAttributes<HTMLElement> {
  /**
   * Add a name of a person or entity to attribute the date line to.
   * Will append a "by [attribution]" suffix to the date line.
   */
  attribution?: string;
  /**
   * Apply exact date formatting instead of relative time.
   */
  exactDate?: boolean;
  /**
   * Icon to display alongside the date line.
   */
  icon?: 'clock' | 'watch';
  /**
   * Prefix label for the date line.
   */
  labelPrefix?: 'posted' | 'updated';
  /**
   * Locale code for localization.
   */
  locale?: string;
  /**
   * HTML tag to use for the date line container.
   */
  tag?: ElementType;
  /**
   * The time to display in the date line.
   */
  time: Date | string | null;
  /**
   * Whether to show a tooltip with the full date and time on hover.
   */
  tooltip?: boolean;
}

const LocalizedDateLine: FC<PropsWithChildren<LocalizedDateLineProps>> = memo(function LocalizedDateLine({
  attribution,
  exactDate = false,
  icon,
  labelPrefix,
  locale = 'en',
  time,
  tag: Tag = 'div',
  tooltip = false,
  className,
  style,
}: LocalizedDateLineProps) {
  const { t } = useTranslation();

  const localizedTime = useMemo(() => {
    if (!time) return null;

    const timeLocaleKeys = getTimeLocaleKeys(!!attribution, labelPrefix);

    if (exactDate) {
      const absoluteTime = formatAbsoluteTime(time, locale);
      return t(`time.absolute.${timeLocaleKeys.relative}`, {
        time: absoluteTime,
        attribution,
      });
    }

    const relativeTime = formatRelativeTime(time, locale);

    return relativeTime === 'just-now'
      ? t(`time.justNow.${timeLocaleKeys.justNow}`, { attribution })
      : t(`time.relative.${timeLocaleKeys.relative}`, { time: relativeTime, attribution });
  }, [attribution, exactDate, labelPrefix, locale, t, time]);

  if (!localizedTime) return null;

  const tagElement = (
    <Tag
      className={`DateLine ${className || ''}`}
      data-testid="localized-date-line"
      style={style}
      suppressHydrationWarning
    >
      {!!icon && <Icon aria-hidden="true" className="DateLine-icon" name={icon} />}
      {localizedTime}
    </Tag>
  );

  if (tooltip) {
    return (
      <Tooltip content={formatAbsoluteTime(time!, locale, { dateStyle: 'medium', timeStyle: 'short' })}>
        {tagElement}
      </Tooltip>
    );
  }

  return tagElement;
});

export default LocalizedDateLine;
